import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useRoom } from '../context/RoomContext'
import { useMedia } from '../context/MediaContext'
import { useTranscript } from '../context/TranscriptContext'
import { useSocket } from '../hooks/useSocket'
import { useCallTimer } from '../hooks/useCallTimer'
import { useTwilioVideo } from '../hooks/useTwilioVideo'
import { useSpeechRecognition } from '../hooks/useSpeechRecognition'
import { API_BASE_URL } from '../utils/api'
import VideoGrid from '../components/video/VideoGrid'
import VideoControls from '../components/video/VideoControls'
// import TranscriptPanel from '../components/transcript/TranscriptPanel'
import TranscriptToggle from '../components/transcript/TranscriptToggle'
import StatusIndicator from '../components/common/StatusIndicator'
import { USER_ROLES } from '../utils/videocall/constants'
import clsx from 'clsx'

export default function VideoRoomPage() {
  const { roomId } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isCreator = searchParams.get('creator') === 'true'
  
  // Parse identity parameters from URL
  const role = searchParams.get('role')
  const userId = searchParams.get('userId')
  const candidateId = searchParams.get('candidateId')
  const candidateName = searchParams.get('name')

  const { initRoom, userRole, isInterviewer } = useRoom()
  const {
    localTracks,
    remoteTracks,
    isVideoEnabled,
    isAudioEnabled,
    toggleVideo,
    toggleAudio,
    stopLocalTracks,
    setLocalTracksFromTwilio,
    setRemoteTracksFromTwilio,
    getLocalVideoTrack,
    getLocalAudioTrack,
    getRemoteVideoTrack,
    getRemoteAudioTrack,
  } = useMedia()

  const {
    entries: transcriptEntries,
    addEntry: addTranscriptEntry,
    clearEntries: clearTranscriptEntries,
  } = useTranscript()

  // Safety check to ensure transcriptEntries is always an array
  const safeTranscriptEntries = transcriptEntries || []

  const { socket, on, off, emit } = useSocket()
  const { seconds: elapsedTime, start: startTimer, stop: stopTimer, reset: resetTimer } = useCallTimer()
  const { 
    isConnected: twilioConnected, 
    isConnecting: twilioConnecting,
    isRecording, 
    connectToRoom, 
    disconnectFromRoom,
    localTracks: twilioLocalTracks,
    remoteTracks: twilioRemoteTracks,
    getLocalVideoTrack: getTwilioLocalVideoTrack,
    getLocalAudioTrack: getTwilioLocalAudioTrack,
    getRemoteVideoTrack: getTwilioRemoteVideoTrack,
    getRemoteAudioTrack: getTwilioRemoteAudioTrack,
    transcriptionEnabled: twilioTranscriptionEnabled,
    twilioTranscripts,
  } = useTwilioVideo(roomId)

  const [connectionStatus, setConnectionStatus] = useState('Connecting...')
  const [isConnected, setIsConnected] = useState(false)
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false)
  const [autoTranscriptionEnabled, setAutoTranscriptionEnabled] =
    useState(false)
  const [remoteAudioMuted, setRemoteAudioMuted] = useState(false)
  const [localSpeaking, setLocalSpeaking] = useState(false)
  const [remoteSpeaking, setRemoteSpeaking] = useState(false)
  const [scheduleError, setScheduleError] = useState(null)
  const [retryTimer, setRetryTimer] = useState(null)
  const [timeRemaining, setTimeRemaining] = useState(null)
  const [isConnecting, setIsConnecting] = useState(false)

  // Store stable isInterviewer result to prevent re-renders
  const isInterviewerRole = isInterviewer()

  // Initialize room
  useEffect(() => {
    if (roomId) {
      // Determine role from URL parameters (new system) or fallback to old system
      let userRole;

      if (role === 'interviewer') {
        userRole = USER_ROLES.INTERVIEWER;
      } else if (role === 'candidate') {
        userRole = USER_ROLES.INTERVIEWEE;
      } else {
        // Fallback to old system for backward compatibility
        userRole = isCreator
          ? USER_ROLES.INTERVIEWER
          : sessionStorage.getItem(`room_creator_${roomId}`) === 'true'
          ? USER_ROLES.INTERVIEWER
          : USER_ROLES.INTERVIEWEE

        if (isCreator) {
          sessionStorage.setItem(`room_creator_${roomId}`, 'true')
        }
      }

      initRoom(roomId, userRole)
    }
  }, [roomId, isCreator, initRoom, role])

  // Connect to Twilio room when room is initialized
  useEffect(() => {
    const attemptConnection = async () => {
      // Enhanced guard: prevent duplicate connections
      if (!roomId || !userRole || twilioConnected || twilioConnecting || scheduleError || isConnecting) {
        return
      }

      try {
        setIsConnecting(true)
        setConnectionStatus('Connecting to video room...')
        setScheduleError(null)

        // Build identity data from URL parameters
        const identityData = {
          userId: userId,
          candidateId: candidateId,
          name: candidateName ? decodeURIComponent(candidateName) : ''
        };

        await connectToRoom(isInterviewerRole, identityData)

        setConnectionStatus('Connected')
        setIsConnected(true)
        startTimer()
      } catch (error) {
        console.error('[Twilio] Error connecting to room:', error)

        // Handle specific error types
        if (error.response?.data?.error === 'TOO_EARLY') {
          const errorData = error.response.data
          setScheduleError({
            type: 'TOO_EARLY',
            message: errorData.message,
            scheduledAt: errorData.scheduledAt,
            canJoinAt: errorData.canJoinAt,
            minutesUntilStart: errorData.minutesUntilStart
          })
          setTimeRemaining(errorData.minutesUntilStart)
          setConnectionStatus('Interview not started yet')

          // Set up auto-retry when interview time arrives
          if (errorData.minutesUntilStart > 0 && errorData.minutesUntilStart <= 60) {
            const retryDelay = errorData.minutesUntilStart * 60 * 1000 // Convert to milliseconds
            const timer = setTimeout(() => {
              setScheduleError(null)
              setIsConnecting(false)
              attemptConnection()
            }, retryDelay)
            setRetryTimer(timer)
          }
        } else {
          setConnectionStatus('Connection failed')
          setScheduleError({
            type: 'CONNECTION_ERROR',
            message: error.message || 'Failed to connect to video room'
          })
        }
      } finally {
        setIsConnecting(false)
      }
    }

    attemptConnection()
  }, [roomId, userRole, isInterviewerRole, twilioConnected, twilioConnecting, scheduleError, connectToRoom, startTimer, userId, candidateId, candidateName])

  // Update media context with Twilio tracks when connection is successful
  useEffect(() => {
    if (twilioConnected && twilioLocalTracks && twilioRemoteTracks) {
      setLocalTracksFromTwilio(twilioLocalTracks)
      setRemoteTracksFromTwilio(twilioRemoteTracks)
    }
  }, [twilioConnected, twilioLocalTracks, twilioRemoteTracks, setLocalTracksFromTwilio, setRemoteTracksFromTwilio])

  // Countdown timer for TOO_EARLY error
  useEffect(() => {
    if (scheduleError?.type === 'TOO_EARLY' && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer)
            // Auto-retry when time is up - but don't reload the page
            setScheduleError(null)
            setIsConnecting(false)
            setConnectionStatus('Retrying connection...')
            return 0
          }
          return prev - 1
        })
      }, 60000) // Update every minute

      return () => clearInterval(timer)
    }
  }, [scheduleError, timeRemaining])

  // Join room when Twilio connection is ready
  useEffect(() => {
    if (socket && twilioConnected && roomId) {
      emit('join-room', roomId)
    }
  }, [socket, twilioConnected, roomId, emit])

  // Auto-enable Web Speech API transcription when Twilio connects
  useEffect(() => {
    if (twilioConnected && isAudioEnabled && !autoTranscriptionEnabled) {
      // Auto-enable Web Speech API transcription
      setTimeout(() => {
        setAutoTranscriptionEnabled(true)

        // Auto-open transcript panel
        setIsTranscriptOpen(true)
      }, 1000)
    }
  }, [twilioConnected, isAudioEnabled, autoTranscriptionEnabled])

  // Update media context when Twilio tracks change
  useEffect(() => {
    if (twilioLocalTracks.length > 0) {
      setLocalTracksFromTwilio(twilioLocalTracks)
    }
  }, [twilioLocalTracks, setLocalTracksFromTwilio])

  useEffect(() => {
    if (twilioRemoteTracks.length > 0) {
      setRemoteTracksFromTwilio(twilioRemoteTracks)
    }
  }, [twilioRemoteTracks, setRemoteTracksFromTwilio])

  // Handle transcript from speech recognition
  const handleTranscriptFinal = useCallback(
    async (text) => {
      const speaker = isInterviewerRole
        ? 'Interviewer'
        : 'Interviewee'

      // Add to local transcript
      addTranscriptEntry(speaker, text, 'local', 'web-speech')

      // Send to remote participant
      if (socket) {
        emit('transcript', {
          roomId,
          speaker,
          text,
          timestamp: Date.now(),
          source: 'web-speech'
        })
      }

      // Save to database via REST API
      try {
        const response = await fetch(`${API_BASE_URL}video/call/transcript/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            roomId,
            participantIdentity: speaker,
            participantSid: null, // Web Speech API doesn't have participant SID
            text,
            timestamp: Date.now(),
            source: 'web-speech',
            metadata: {
              partial_results: false,
              stability: 1.0,
              language_code: 'en-US',
              sequence_number: null,
              trackSid: null,
              version: '1.0'
            }
          })
        })

        if (!response.ok) {
          console.error('[Transcript] ❌ Database save failed with status:', response.status)
        }
      } catch (error) {
        console.error('[Transcript] ❌ Failed to save Web Speech API transcript to database:', error)
      }
    },
    [isInterviewerRole, addTranscriptEntry, socket, emit, roomId]
  )

  // Handle Twilio transcript
  const handleTwilioTranscript = useCallback(
    async (transcriptData) => {
      const { participant, transcription, timestamp, partial_results, stability, participantSid, trackSid, language_code, sequence_number, version } = transcriptData

      // Only save final results to avoid duplicates from accumulated partial results
      if (!partial_results) {
        const speaker = participant === 'interviewer' ? 'Interviewer' : 'Interviewee'

        // Add to local transcript
        if (isTranscriptOpen) {
          addTranscriptEntry(speaker, transcription, timestamp, 'twilio', 'twilio')
        }

        // Send to remote participant via socket (existing functionality)
        if (socket) {
          emit('transcript', {
            roomId,
            speaker,
            text: transcription,
            timestamp: timestamp,
            source: 'twilio'
          })
        }

        // Save to database via REST API
        try {
          await fetch(`${API_BASE_URL}video/call/transcript/save`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              roomId,
              participantIdentity: speaker,
              participantSid,
              text: transcription,
              timestamp,
              source: 'twilio',
              metadata: {
                partial_results,
                stability,
                language_code,
                sequence_number,
                trackSid,
                version
              }
            })
          })
        } catch (error) {
          console.error('[Twilio Transcript] ❌ Failed to save to database:', error)
        }
      }
    },
    [isTranscriptOpen, addTranscriptEntry, socket, emit, roomId]
  )

  // Process Twilio transcripts when they arrive
  useEffect(() => {
    if (twilioTranscripts && twilioTranscripts.length > 0) {
      const latestTranscript = twilioTranscripts[twilioTranscripts.length - 1]
      handleTwilioTranscript(latestTranscript)
    }
  }, [twilioTranscripts, handleTwilioTranscript])

  // Speech recognition
  const { status: transcriptStatus } = useSpeechRecognition({
    onTranscriptFinal: handleTranscriptFinal,
    isEnabled: isTranscriptOpen || autoTranscriptionEnabled,
    isAudioEnabled,
  })

  // Socket event handlers
  useEffect(() => {
    if (!socket) return

    // Room joined
    on('room-joined', (data) => {
      setConnectionStatus('Waiting for participant...')
    })

    // User connected
    on('user-connected', () => {
      setConnectionStatus('Participant joined')

      // Auto-enable transcription after participant connects
      setTimeout(() => {
        if (!autoTranscriptionEnabled && isAudioEnabled) {
          setAutoTranscriptionEnabled(true)

          // Auto-open panel for both interviewer and interviewee to see transcripts
          if (!isTranscriptOpen) {
            setIsTranscriptOpen(true)
          }
        }
      }, 2000)
    })

    // User disconnected
    on('user-disconnected', () => {
      setConnectionStatus('Participant left')
      setIsConnected(false)
      setRemoteTracksFromTwilio([])
      stopTimer()
    })

    // Audio state from remote
    on('audio-state', (data) => {
      setRemoteAudioMuted(data.muted)

      if (isTranscriptOpen) {
        const remoteSpeaker = isInterviewerRole ? 'Interviewee' : 'Interviewer'
        const message = data.muted
          ? `🔇 ${remoteSpeaker} muted their microphone`
          : `🎤 ${remoteSpeaker} unmuted their microphone`
        addTranscriptEntry('System', message, Date.now(), 'system')
      }
    })

    // Transcript from remote
    on('transcript', (data) => {
      const myRole = isInterviewerRole ? 'Interviewer' : 'Interviewee'
      if (data.speaker !== myRole && isTranscriptOpen) {
        addTranscriptEntry(
          data.speaker,
          data.text,
          data.timestamp || Date.now(),
          'remote'
        )
      }
    })

    // Recording notifications (Twilio handles recording automatically)
    on('start-recording', () => {
      // Recording started
    })

    on('stop-recording', () => {
      // Recording stopped
    })

    return () => {
      off('room-joined')
      off('user-connected')
      off('user-disconnected')
      off('audio-state')
      off('transcript')
      off('start-recording')
      off('stop-recording')
    }
  }, [
    socket,
    on,
    off,
    emit,
    isInterviewerRole,
    isTranscriptOpen,
    isAudioEnabled,
    autoTranscriptionEnabled,
    addTranscriptEntry,
    setRemoteTracksFromTwilio,
    stopTimer,
  ])

  // Handle recording start (Twilio handles this automatically)
  const handleStartRecording = useCallback(() => {
    emit('start-recording', { roomId })
  }, [emit, roomId])

  // Handle recording stop
  const handleStopRecording = useCallback(async () => {
    try {
      emit('stop-recording', { roomId })
      // Twilio recording stops when room is completed
    } catch (error) {
      console.error('[Recording] Failed to stop:', error)
      alert('Failed to stop recording: ' + error.message)
    }
  }, [emit, roomId])

  // Handle end call
  const handleEndCall = useCallback(() => {
    if (!window.confirm('Are you sure you want to end the call?')) return

    if (isRecording) {
      handleStopRecording()
    }

    disconnectFromRoom()
    stopLocalTracks()
    stopTimer()
    resetTimer()
    clearTranscriptEntries()

    if (socket) {
      socket.disconnect()
    }

    navigate('/')
  }, [
    isRecording,
    handleStopRecording,
    disconnectFromRoom,
    stopLocalTracks,
    stopTimer,
    resetTimer,
    clearTranscriptEntries,
    socket,
    navigate,
  ])

  // Toggle transcript panel
  const handleToggleTranscript = useCallback(() => {
    setIsTranscriptOpen((prev) => !prev)
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnectFromRoom()
      stopLocalTracks()
      stopTimer()
      if (retryTimer) {
        clearTimeout(retryTimer)
      }
    }
  }, [disconnectFromRoom, stopLocalTracks, stopTimer, retryTimer])

  const localParticipant = {
    name: 'You',
    role: isInterviewerRole ? 'Interviewer' : 'Interviewee',
    avatar: isInterviewerRole ? 'I' : 'C',
  }

  const remoteParticipant = {
    name: 'Participant',
    role: isInterviewerRole ? 'Interviewee' : 'Interviewer',
    avatar: isInterviewerRole ? 'C' : 'I',
  }

  // Helper function to format time remaining
  const formatTimeRemaining = (minutes) => {
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`
    }
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    if (remainingMinutes === 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`
    }
    return `${hours} hour${hours !== 1 ? 's' : ''} ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`
  }

  // Helper function to format date/time
  const formatDateTime = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short'
    })
  }


  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ background: 'var(--dark-bg)' }}>
      
      {/* Header - Hidden on mobile */}
      <header className="header max-md:hidden">
        <div className="room-info">
          <div className="room-badge">
            Interview Room
          </div>
          <div className="connection-status">
            <div className="status-dot" />
            <span>Room: {roomId?.substring(0, 8)}...</span>
          </div>
        </div>
        <div className="header-right">
          <StatusIndicator
            status={connectionStatus}
            isConnected={isConnected}
          />
          {/* Recording Indicator in Header */}
          {isRecording && (
            <div className="recording-indicator active">
              <div className="recording-dot" />
              <span>Recording in Progress</span>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="main-container">
        {scheduleError ? (
          <div className="flex-1 flex items-center justify-center p-4">
            <div style={{ 
              background: 'var(--glass-bg)', 
              backdropFilter: 'blur(20px)', 
              border: '1px solid var(--glass-border)',
              borderRadius: '20px',
              padding: '2rem',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              {scheduleError.type === 'TOO_EARLY' ? (
                <div className="text-center">
                  <div className="text-6xl mb-6">⏰</div>
                  <h2 style={{ color: 'var(--text-primary)', fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
                    Interview Not Started Yet
                  </h2>
                  <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem', lineHeight: '1.6' }}>
                    {scheduleError.message}
                  </p>
                  
                  <div style={{ 
                    background: 'rgba(59, 130, 246, 0.1)', 
                    backdropFilter: 'blur(10px)', 
                    border: '1px solid rgba(59, 130, 246, 0.3)', 
                    borderRadius: '12px', 
                    padding: '1.5rem', 
                    marginBottom: '1.5rem' 
                  }}>
                    <div style={{ color: 'rgba(59, 130, 246, 0.8)', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.75rem' }}>
                      <strong>Scheduled Time:</strong>
                    </div>
                    <div style={{ color: 'rgba(59, 130, 246, 0.9)', fontWeight: '600', fontSize: '1.125rem' }}>
                      {formatDateTime(scheduleError.scheduledAt)}
                    </div>
                  </div>
                  
                  <div style={{ 
                    background: 'rgba(245, 158, 11, 0.1)', 
                    backdropFilter: 'blur(10px)', 
                    border: '1px solid rgba(245, 158, 11, 0.3)', 
                    borderRadius: '12px', 
                    padding: '1.5rem', 
                    marginBottom: '2rem' 
                  }}>
                    <div style={{ color: 'rgba(245, 158, 11, 0.8)', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                      <strong>Time Remaining:</strong>
                    </div>
                    <div style={{ color: 'rgba(245, 158, 11, 0.9)', fontWeight: 'bold', fontSize: '1.5rem' }}>
                      {formatTimeRemaining(timeRemaining || scheduleError.minutesUntilStart)}
                    </div>
                  </div>
                  
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '2rem' }}>
                    You can join the interview at the scheduled time.
                  </div>
                  
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => {
                        setScheduleError(null)
                        setIsConnecting(false)
                        setConnectionStatus('Retrying connection...')
                      }}
                      style={{
                        padding: '0.75rem 2rem',
                        borderRadius: '12px',
                        background: 'var(--primary-gradient)',
                        border: '1px solid rgba(102, 126, 234, 0.3)',
                        color: 'white',
                        fontWeight: '600',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.05)'
                        e.target.style.boxShadow = '0 10px 30px rgba(102, 126, 234, 0.4)'
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)'
                        e.target.style.boxShadow = 'none'
                      }}
                    >
                      Try Again Now
                    </button>
                    <button
                      onClick={() => window.location.reload()}
                      style={{
                        padding: '0.75rem 2rem',
                        borderRadius: '12px',
                        background: 'var(--glass-bg)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid var(--glass-border)',
                        color: 'var(--text-primary)',
                        fontWeight: '600',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.05)'
                        e.target.style.background = 'rgba(255, 255, 255, 0.1)'
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)'
                        e.target.style.background = 'var(--glass-bg)'
                      }}
                    >
                      Refresh Page
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <div className="text-6xl mb-6">❌</div>
                  <h2 style={{ color: 'var(--text-primary)', fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
                    Connection Error
                  </h2>
                  <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem', lineHeight: '1.6' }}>
                    {scheduleError.message}
                  </p>
                  <button
                    onClick={() => window.location.reload()}
                    style={{
                      padding: '0.75rem 2rem',
                      borderRadius: '12px',
                      background: 'var(--primary-gradient)',
                      border: '1px solid rgba(102, 126, 234, 0.3)',
                      color: 'white',
                      fontWeight: '600',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.05)'
                      e.target.style.boxShadow = '0 10px 30px rgba(102, 126, 234, 0.4)'
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)'
                      e.target.style.boxShadow = 'none'
                    }}
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="video-grid">
            <VideoGrid
              localVideoTrack={getLocalVideoTrack()}
              localAudioTrack={getLocalAudioTrack()}
              remoteVideoTrack={getRemoteVideoTrack()}
              remoteAudioTrack={getRemoteAudioTrack()}
              localParticipant={localParticipant}
              remoteParticipant={remoteParticipant}
              localMuted={!isAudioEnabled}
              remoteMuted={remoteAudioMuted}
              localSpeaking={localSpeaking}
              remoteSpeaking={remoteSpeaking}
            />
          </div>
        )}
      </main>



      {/* Transcript Toggle (Desktop - Interviewer only) */}
      {isInterviewerRole && typeof window !== 'undefined' && window.innerWidth > 768 && (
        <TranscriptToggle
          isOpen={isTranscriptOpen}
          onClick={handleToggleTranscript}
          isPanelOpen={isTranscriptOpen}
          className="flex max-md:hidden"
        />
      )}


      {/* Transcript Panel - Commented out to prevent layout issues */}
      {/* <TranscriptPanel
        entries={safeTranscriptEntries}
        status={transcriptStatus}
        isOpen={isTranscriptOpen}
        isEmpty={safeTranscriptEntries.length === 0}
      /> */}

      {/* Video Controls - Hide when there's a schedule error */}
      {!scheduleError && (
        <div className="control-bar">
          <VideoControls
            isVideoEnabled={isVideoEnabled}
            isAudioEnabled={isAudioEnabled}
            isRecording={isRecording}
            isInterviewer={isInterviewerRole}
            onToggleVideo={toggleVideo}
            onToggleAudio={toggleAudio}
            onStartRecording={handleStartRecording}
            onStopRecording={handleStopRecording}
            onToggleTranscript={handleToggleTranscript}
            onEndCall={handleEndCall}
          />
        </div>
      )}
    </div>
  )
}
